import React, { useEffect, useMemo, useRef, useState } from "react"; 
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faLayerGroup,
  faUsers,
  faMoneyBillWave,
  faCheckCircle,
  faClock,
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Card,
  Table,
  InputGroup,
  Form,
  Badge,
  Button,
} from "@themesberg/react-bootstrap";
import { userLevelEarnings } from "../../../pages/api";

const levelIncomeData = [];

const formatCurrency = (value) => {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
};

const getStatusBadge = (status) => {
  switch (status) {
    case "Paid":
      return "success";
    case "Pending":
      return "warning";
    case "Unpaid":
      return "danger";
    default:
      return "secondary";
  }
};

// const formatDateTimeToMinutes = (value) => {
//   if (!value) return "N/A" || "-";

//   const date = new Date(value);
//   if (Number.isNaN(date.getTime())) return value;

//   return new Intl.DateTimeFormat("en-IN", {
//     day: "2-digit",
//     month: "2-digit",
//     year: "numeric",
//     hour: "2-digit",
//     minute: "2-digit",
//     hour12: false,
//   }).format(date);
// };

const formatDateOnly = (value) => {
  if (!value) return "N/A" || "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const mapLevelItem = (item, index) => {
  const commission = Number(item?.commission_earned || 0);
  const settlementDate = item?.settlement_date || null;
  const levelLabel = String(item?.level || `Level ${index + 1}`);

  return {
    id: `${levelLabel}-${index}`,
    levelNumber: levelLabel,
    usersCount: Number(item?.users_count || 0),
    businessGenerated: Number(item?.business_generated || 0),
    commissionEarned: commission,
    status:
      commission > 0
        ? settlementDate
          ? "Paid"
          : "Pending"
        : "Unpaid",
    settlementDate: formatDateOnly(settlementDate),
  };
};

export default function UserLevelEarning() {
  const [search, setSearch] = useState("");
  const [levels, setLevels] = useState(levelIncomeData);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const searchDebounceRef = useRef(null);
  const firstSearchRenderRef = useRef(true);

  const fetchLevelEarnings = async (page = 1, perPage = 10, searchTerm = "") => {
    try {
      setLoading(true);

      const response = await userLevelEarnings({
        page,
        per_page: perPage,
        search: searchTerm,
      });

      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiLevels = Array.isArray(apiData?.levels) ? apiData.levels : [];
      const apiPagination = apiData?.pagination || {};

      setLevels(apiLevels.map(mapLevelItem));
      setPagination({
        current_page: Number(apiPagination.current_page ?? page) || page,
        last_page: Number(apiPagination.last_page ?? 1) || 1,
        per_page: Number(apiPagination.per_page ?? perPage) || perPage,
        total: Number(apiPagination.total ?? apiLevels.length) || apiLevels.length,
      });
    } catch (error) {
      console.error("Failed to fetch level earnings:", error);
      toast.error(
        error?.response?.data?.message || "Unable to fetch level earnings."
      );
      setLevels([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevelEarnings(1, pagination.per_page, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstSearchRenderRef.current) {
      firstSearchRenderRef.current = false;
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchLevelEarnings(1, pagination.per_page, search.trim());
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const filteredData = useMemo(() => {
    return levels;
  }, [levels]);

  const totalUsers = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + Number(item.usersCount || 0), 0);
  }, [filteredData]);

  const totalBusiness = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + Number(item.businessGenerated || 0), 0);
  }, [filteredData]);

  const totalCommission = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + Number(item.commissionEarned || 0), 0);
  }, [filteredData]);

  const paidCount = useMemo(() => {
    return filteredData.filter((item) => item.status === "Paid").length;
  }, [filteredData]);

  const getShowingRange = () => {
    const total = Number(pagination.total || 0);
    const current = Number(pagination.current_page || 1);
    const perPage = Number(pagination.per_page || 10);

    if (total === 0) {
      return { start: 0, end: 0 };
    }

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Start pages
    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    // End pages
    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    // Middle pages: 1 ... previous current next ... last
    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();

  return (
    <>
      <div className="lsg-page">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 py-2 mb-3">
        <div>
          <h2 className="lsg-page-title">Level Earnings</h2>
          <p className="lsg-page-subtitle">Level-wise commission and settlement details.</p>
        </div>

        <InputGroup style={{ maxWidth: "360px" }}>
          <InputGroup.Text className="bg-white">
            <FontAwesomeIcon icon={faSearch} className="text-muted" />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            style={{ boxShadow: "none" }}
          />
        </InputGroup>
      </div>

      {/* <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div>
          <h2 className="h4 mb-1">Level Earnings</h2>
          <div className="text-muted">Level-wise commission and settlement details.</div>
        </div>
      </div> */}

      {/* <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h5 className="mb-1">Level Earnings</h5>
              <div className="text-muted small">Level income overview</div>
            </div>

            <InputGroup style={{ maxWidth: "360px" }}>
              <InputGroup.Text className="bg-white">
                <FontAwesomeIcon icon={faSearch} className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ boxShadow: "none" }}
              />
            </InputGroup>
          </div>
        </Card.Body>
      </Card> */}

      {/* <Row className="g-4 mb-4">
        <Col xs={12} sm={6} xl={3}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Total Levels</div>
                  <h3 className="mb-0">{filteredData.length}</h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faLayerGroup} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={3}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Total Users</div>
                  <h3 className="mb-0">{totalUsers}</h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faUsers} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={3}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Business Generated</div>
                  <h3 className="mb-0">{formatCurrency(totalBusiness)}</h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={3}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Commission Earned</div>
                  <h3 className="mb-0">{formatCurrency(totalCommission)}</h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row> */}

      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h5 className="mb-1">Level Income List</h5>
              {/* <div className="text-muted small">
                Level-wise income, commission, and settlement details
              </div> */}
            </div>
          </div>

          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Level Number</th>
                  <th>Users Count</th>
                  <th className="text-end">Business Generated</th>
                  <th className="text-end">Commission Earned</th>
                  <th>Status</th>
                  <th>Settlement Date</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-5">
                      Loading level earnings...
                    </td>
                  </tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-semibold">{item.levelNumber}</td>
                      <td>{item.usersCount}</td>
                      <td className="text-end fw-bold">
                        {formatCurrency(item.businessGenerated)}
                      </td>
                      <td className="text-end fw-bold">
                        {formatCurrency(item.commissionEarned)}
                      </td>
                      <td>
                        <Badge
                          bg={getStatusBadge(item.status)}
                          className="rounded-pill d-inline-flex align-items-center justify-content-center"
                          style={{
                            minWidth: "5rem",
                            height: "2rem",
                            padding: "0 12px",
                          }}
                        >
                          {item.status}
                        </Badge>
                      </td>
                      <td className="text-muted">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                        {item.settlementDate}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-5">
                      No level income records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loading}
                onClick={() =>
                  fetchLevelEarnings(
                    Math.max(1, pagination.current_page - 1),
                    pagination.per_page,
                    search.trim()
                  )
                }
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {getPageItems().map((item, index) =>
                item === "..." ? (
                  <span key={`dots-${index}`} className="text-muted px-1">
                    ...
                  </span>
                ) : (
                  <Button
                    key={item}
                    type="button"
                    variant="outline-primary"
                    disabled={loading}
                    onClick={() => fetchLevelEarnings(item, pagination.per_page, search.trim())}
                    className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""
                      }`}
                  >
                    {item}
                  </Button>
                )
              )}

              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page >= pagination.last_page || loading}
                onClick={() =>
                  fetchLevelEarnings(
                    Math.min(pagination.last_page, pagination.current_page + 1),
                    pagination.per_page,
                    search.trim()
                  )
                }
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      </div>
    </>
  );
}